import cn from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';

import addSpreadOperation from 'editor/src/store/design/operation/spreadManipulation/addSpreadOperation';
import removeSpreadOperation from 'editor/src/store/design/operation/spreadManipulation/removeSpreadOperation';
import getPagesPerStep from 'editor/src/store/design/selector/getPagesPerStep';
import { PageStepDirection } from 'editor/src/store/design/types';
import getSpreadCountForIncrement from 'editor/src/store/design/util/getSpreadCountForIncrement';
import {
  legacyCanAddPagesForSpread,
  legacyCanRemovePagesForSpread,
  legacyGetSpreadCountForIncrement,
} from 'editor/src/store/design/util/legacyPageCountRules';
import { canAddPagesForSpread, canRemovePagesForSpread } from 'editor/src/store/design/util/pageCountControls';
import resolveTemplateSpreadIndex from 'editor/src/store/design/util/resolveTemplateSpreadIndex';
import spreadRemovalHoldsContent from 'editor/src/store/design/util/spreadRemovalHoldsContent';
import setCurrentSpreadIndexOperation from 'editor/src/store/editor/operation/setCurrentSpreadIndexOperation';
import { useDispatch, useSelector, useStore } from 'editor/src/store/hooks';
import isPageCountOptionsEnabled from 'editor/src/store/hostSettings/selector/isPageCountOptionsEnabled';
import getECommercePageCounts from 'editor/src/store/variants/selector/getECommercePageCounts';

import useTr from 'editor/src/util/useTr';

import ConfirmationMenu from '../ConfirmationMenu';
import { isDisplayPageActive } from '../EditorArea/utils/displayPageUtils';
import useSpreadReorder from '../EditorArea/utils/useSpreadReorder';
import useGetSpreadList from '../MobileLayout/useGetSpreadList';
import PageCounter from '../PageCounter';
import SpreadMobileMenu from '../SpreadMobileMenu';
import { useCanvasRendering } from '../SpreadPreview';
import { canShowAddRemovePages } from '../TopMenuDesktop/buttons/ButtonAddPages';
import usePageCountLimit from '../TopMenuDesktop/usePageCountLimit';
import useMobileMenu from '../useMobileMenu';

import Spread from './Spread';

import styles from './index.module.scss';

interface Props {
  onSpreadClick: () => void;
}

function SpreadListView({ onSpreadClick }: Props) {
  const focusedContentAddress = useSelector((state) => state.editor.focusedContentAddress);
  const canAddOrRemoveSpreads = useSelector((state) => canShowAddRemovePages(state));
  const pageCountOptionsEnabled = useSelector(isPageCountOptionsEnabled);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const tr = useTr();
  const store = useStore();

  const actionsMenu = useMobileMenu();
  const confirmationMenu = useMobileMenu();

  // The pages the confirmation names: the ones the removal plan takes, as on desktop.
  const [pagesToRemove, setPagesToRemove] = useState(0);

  // Same rule as the desktop menu: pages that hold nothing of the user's own go without a question.
  const handleSpreadRemoval = useCallback(() => {
    actionsMenu.close();
    if (!pageCountOptionsEnabled) {
      confirmationMenu.open();
      return;
    }
    const { pages, holdsContent } = spreadRemovalHoldsContent(store.getState(), currentSpreadIndexRef.current);
    if (holdsContent) {
      setPagesToRemove(pages);
      confirmationMenu.open();
    } else {
      dispatch(removeSpreadOperation(currentSpreadIndexRef.current));
    }
  }, [actionsMenu.close, confirmationMenu.open, store, pageCountOptionsEnabled]);

  const handleSpreadRemovalConfirmation = useCallback(() => {
    dispatch(removeSpreadOperation(currentSpreadIndexRef.current));
    confirmationMenu.close();
  }, [confirmationMenu.close]);

  const handleAddSpread = useCallback(() => {
    dispatch(addSpreadOperation(currentSpreadIndexRef.current));
    actionsMenu.close();
  }, [actionsMenu.close]);

  const handleDuplicateSpread = useCallback(() => {
    dispatch(addSpreadOperation(currentSpreadIndexRef.current, true));
    actionsMenu.close();
  }, [actionsMenu.close]);

  const handleActionMenuClose = useCallback(() => {
    actionsMenu.close();
  }, [actionsMenu.close]);

  const handleConfirmationMenuClose = useCallback(() => {
    confirmationMenu.close();
  }, [confirmationMenu.close]);

  const handleSpreadEdit = useCallback(() => {
    dispatch(setCurrentSpreadIndexOperation(currentSpreadIndexRef.current));
    actionsMenu.close();
    onSpreadClick();
  }, [onSpreadClick, actionsMenu.close]);

  const currentSpreadIndex = useSelector((state) => state.editor.currentSpreadIndex);
  const designData = useSelector((state) => state.design.designData);
  const spreads = designData?.spreads || [];
  const { spreadsList, designKey } = useGetSpreadList();
  const currentSpreadIndexRef = useRef(-1);

  const { requestRender } = useCanvasRendering(spreadsList.length > 1);

  const handleSpreadClick = useCallback(
    (spreadIndex: number) => {
      dispatch(setCurrentSpreadIndexOperation(spreadIndex));
      onSpreadClick();
    },
    [onSpreadClick],
  );

  const handleLongPress = useCallback(
    (spreadIndex: number) => {
      currentSpreadIndexRef.current = spreadIndex;
      actionsMenu.open();
    },
    [actionsMenu.open],
  );

  const { reorderableSpreadIndexes, handleOrderChange } = useSpreadReorder(spreadsList);

  const pageCountLimit = usePageCountLimit();

  const canRemoveSpread = useCallback(
    (spreadIndex) =>
      pageCountOptionsEnabled
        ? canRemovePagesForSpread(designData, !!spreads[spreadIndex]?.canRemove, pageCountLimit, spreadIndex)
        : legacyCanRemovePagesForSpread(designData, !!spreads[spreadIndex]?.canRemove, pageCountLimit),
    [spreads, designData, pageCountLimit, pageCountOptionsEnabled],
  );

  const canAddAfterSpread = useCallback(
    (spreadIndex) =>
      pageCountOptionsEnabled
        ? canAddPagesForSpread(designData, !!spreads[spreadIndex]?.canAddAfter, pageCountLimit, spreadIndex)
        : legacyCanAddPagesForSpread(designData, !!spreads[spreadIndex]?.canAddAfter, pageCountLimit),
    [spreads, designData, pageCountLimit, pageCountOptionsEnabled],
  );

  const pagesPerStepFor = useCallback(
    (spreadIndex: number, direction: PageStepDirection) =>
      spreadIndex >= 0 ? getPagesPerStep(store.getState(), spreadIndex, direction) : 0,
    [store],
  );

  // "Duplicate" copies exactly this spread, so it only makes sense when one
  // add/remove step is a single spread. Hide it (as on desktop) with no design
  // and when the print grid forces a step to insert several spreads at once.
  const canDuplicateSpread = useCallback(
    (spreadIndex: number) => {
      const state = store.getState();
      const design = state.design.designData;
      if (!design || spreadIndex < 0) {
        return false;
      }
      if (!isPageCountOptionsEnabled(state)) {
        return legacyGetSpreadCountForIncrement(design, spreadIndex, getECommercePageCounts(state)) === 1;
      }
      const templateIndex = resolveTemplateSpreadIndex(design, spreadIndex) ?? spreadIndex;
      return getSpreadCountForIncrement(design, templateIndex, 'add', getECommercePageCounts(state)) === 1;
    },
    [store],
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source } = result;
      handleOrderChange(source, destination);
    },
    [handleOrderChange],
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided) => {
          const list = (
            <>
              <div
                className={cn(styles.SpreadListView, 'cy-spreads-list-wrapper')}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {spreadsList.map((spread, index) => {
                  return (
                    <Draggable
                      key={index}
                      draggableId={index.toString()}
                      index={index}
                      isDragDisabled={!reorderableSpreadIndexes.has(spread.spreadIndex)}
                      shouldRespectForcePress
                    >
                      {(provided) => (
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        <div
                          key={spread.spreadIndex}
                          ref={provided.innerRef}
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...provided.draggableProps}
                        >
                          <Spread
                            canAddAfter={canAddAfterSpread(spread.spreadIndex)}
                            displayPage={spread}
                            requestRender={requestRender}
                            designKey={designKey}
                            active={isDisplayPageActive(spread, currentSpreadIndex, focusedContentAddress)}
                            onSpreadClick={handleSpreadClick}
                            onSpreadLongPress={handleLongPress}
                            onMoreClick={handleLongPress}
                            canAddOrRemoveSpreads={canAddOrRemoveSpreads}
                            dragHandleProps={provided.dragHandleProps}
                            isDragDisabled={!reorderableSpreadIndexes.has(spread.spreadIndex)}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
              {actionsMenu.render(
                <SpreadMobileMenu
                  canAddAfter={canAddAfterSpread(currentSpreadIndexRef.current)}
                  canDuplicate={canDuplicateSpread(currentSpreadIndexRef.current)}
                  canRemove={canRemoveSpread(currentSpreadIndexRef.current)}
                  pagesPerAddStep={pagesPerStepFor(currentSpreadIndexRef.current, 'add')}
                  pagesPerRemoveStep={pagesPerStepFor(currentSpreadIndexRef.current, 'remove')}
                  onClose={handleActionMenuClose}
                  onAdd={handleAddSpread}
                  onEdit={handleSpreadEdit}
                  onDuplicate={handleDuplicateSpread}
                  onRemove={handleSpreadRemoval}
                />,
              )}
              {confirmationMenu.render(
                pageCountOptionsEnabled ? (
                  <ConfirmationMenu
                    headerText={t('editor-delete-pages-confirm-count', { count: pagesToRemove })}
                    headerDescriptionText={tr(
                      'editor-delete-pages-content-warning',
                      'These pages hold your photos or text.',
                    )}
                    confirmButtonText={t('editor-delete-pages-count', { count: pagesToRemove })}
                    cancellButtonText={t('editor-cancel')}
                    onCancel={handleConfirmationMenuClose}
                    onConfirm={handleSpreadRemovalConfirmation}
                    onCrossClick={handleConfirmationMenuClose}
                  />
                ) : (
                  <ConfirmationMenu
                    headerText={t('editor-delete-pages-confirm-count', {
                      count: pagesPerStepFor(currentSpreadIndexRef.current, 'remove'),
                    })}
                    confirmButtonText={t('editor-delete-pages-count', {
                      count: pagesPerStepFor(currentSpreadIndexRef.current, 'remove'),
                    })}
                    cancellButtonText={t('No')}
                    onCancel={handleConfirmationMenuClose}
                    onConfirm={handleSpreadRemovalConfirmation}
                    onCrossClick={handleConfirmationMenuClose}
                  />
                ),
              )}
            </>
          );

          return pageCountOptionsEnabled ? (
            // One grid item, so the readout does not take a row of the mobile layout's grid of its own.
            <div className={styles.container}>
              <PageCounter className={styles.pageCounter} />
              {list}
            </div>
          ) : (
            list
          );
        }}
      </Droppable>
    </DragDropContext>
  );
}

export default React.memo(SpreadListView);
